/**
 * LiveShipmentMap — embeddable Leaflet map of all in-transit dispatches.
 *
 * Data: GET /api/dispatch/live-map (polled every 30s while mounted).
 * Each dispatch is plotted with a CircleMarker coloured by status, a dashed
 * line from origin→destination, and a popup showing customer/container/ETA.
 *
 * Tile layer: OpenStreetMap (free, no API key). Attribution per OSM policy.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Polyline, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet.heat';
import axios from 'axios';
import { Truck, ArrowsClockwise, MapPin, Fire } from '@phosphor-icons/react';
import FleetKpiStrip from './FleetKpiStrip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30_000;

const STATUS_COLOURS = {
  sealed: '#10b981',
  dispatched: '#0ea5e9',
  delivered: '#22c55e',
  loading: '#f59e0b',
  planned: '#94a3b8',
};

// Heat-map overlay — managed imperatively inside Leaflet, hidden when `points` empty.
const HeatLayer = ({ points }) => {
  const map = useMap();
  const layerRef = useRef(null);
  useEffect(() => {
    if (!points || points.length === 0) {
      if (layerRef.current) { layerRef.current.remove(); layerRef.current = null; }
      return;
    }
    const arr = points.map(p => [p.lat, p.lng, p.intensity ?? 0.5]);
    if (layerRef.current) {
      layerRef.current.setLatLngs(arr);
    } else {
      layerRef.current = L.heatLayer(arr, {
        radius: 28, blur: 22, minOpacity: 0.25, maxZoom: 12,
        gradient: { 0.2: '#0ea5e9', 0.4: '#10b981', 0.6: '#f59e0b', 0.85: '#ef4444' },
      }).addTo(map);
    }
    return () => { if (layerRef.current) { layerRef.current.remove(); layerRef.current = null; } };
  }, [points, map]);
  return null;
};

const LiveShipmentMap = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshAt, setRefreshAt] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  // Heatmap overlay (Phase 3f)
  const [heatEnabled, setHeatEnabled] = useState(false);
  const [heatData, setHeatData] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/dispatch/live-map`, { withCredentials: true });
      setData(r.data);
      setRefreshAt(new Date());
    } catch {
      // silently ignore on dashboard
    } finally { setLoading(false); }
  };

  const loadHeat = async () => {
    try {
      const r = await axios.get(`${API}/dispatch/gps-heatmap?days=30`, { withCredentials: true });
      setHeatData(r.data);
    } catch { setHeatData({ points: [] }); }
  };

  useEffect(() => {
    const t = setTimeout(() => setMapReady(true), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (heatEnabled && !heatData) loadHeat();
  }, [heatEnabled, heatData]);

  const center = useMemo(() => {
    const rows = data?.dispatches || [];
    if (!rows.length) return [-28.0, 135.0]; // centre of AU
    const avg = rows.reduce((acc, r) => ({ lat: acc.lat + r.current.lat, lng: acc.lng + r.current.lng }),
      { lat: 0, lng: 0 });
    return [avg.lat / rows.length, avg.lng / rows.length];
  }, [data]);

  return (
    <div data-testid="live-shipment-map-widget">
      <FleetKpiStrip />
      <div className="bg-card border border-border rounded-lg">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted">
        <div className="flex items-center gap-2">
          <div className="bg-brand text-white p-1.5">
            <MapPin size={14} weight="fill" />
          </div>
          <div>
            <p className="overline text-muted-foreground">Live Tracking</p>
            <h2 className="text-sm font-bold tracking-tight uppercase">
              Live Shipment Map
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="font-mono" data-testid="live-shipment-count">
            <Truck size={12} weight="bold" className="inline mr-1" />
            {data?.total ?? 0} in-transit
          </span>
          <button type="button" onClick={() => setHeatEnabled(v => !v)}
            data-testid="live-shipment-heat-toggle"
            className={`px-2 py-1 border text-[10px] font-bold uppercase tracking-widest transition ${
              heatEnabled
                ? 'bg-orange-600 text-white border-orange-600 hover:bg-orange-700'
                : 'border-border hover:border-orange-500 hover:text-orange-600'
            }`}
            title="Toggle POP GPS density heat-map (last 30 days)">
            <Fire size={11} weight="bold" className="inline mr-1" />
            {heatEnabled ? `Heat-map · ${heatData?.point_count ?? '…'}` : 'Heat-map'}
          </button>
          <button type="button" onClick={load} disabled={loading}
            data-testid="live-shipment-refresh"
            className="px-2 py-1 border border-border hover:border-brand hover:text-brand text-[10px] font-bold uppercase tracking-widest">
            <ArrowsClockwise size={11} weight="bold" className="inline mr-1" />
            Refresh
          </button>
          {refreshAt && (
            <span className="font-mono text-[10px] text-muted-foreground" data-testid="live-shipment-refresh-at">
              {refreshAt.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      <div style={{ height: 380, width: '100%' }}>
        {mapReady && (
        <MapContainer
          center={center}
          zoom={4}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
          data-testid="live-shipment-leaflet"
        >
          <TileLayer
            url={data?.tile_url || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'}
            attribution={data?.attribution || '© OpenStreetMap contributors'}
          />
          {heatEnabled && heatData?.points && (
            <HeatLayer points={heatData.points} />
          )}
          {(data?.dispatches || []).map((d) => {
            const color = STATUS_COLOURS[d.status] || '#475569';
            return (
              <React.Fragment key={d.dispatch_id}>
                <Polyline
                  positions={[[d.origin.lat, d.origin.lng], [d.destination_gps.lat, d.destination_gps.lng]]}
                  pathOptions={{ color, weight: 1.5, opacity: 0.35, dashArray: '4 5' }}
                />
                <CircleMarker
                  center={[d.destination_gps.lat, d.destination_gps.lng]}
                  radius={3}
                  pathOptions={{ color, fillColor: color, fillOpacity: 0.4, weight: 1 }}
                />
                <CircleMarker
                  center={[d.current.lat, d.current.lng]}
                  radius={7}
                  pathOptions={{ color, fillColor: color, fillOpacity: 0.85, weight: 2 }}
                  data-testid={`live-shipment-marker-${d.dispatch_number}`}
                >
                  <Popup>
                    <div style={{ minWidth: 200, fontFamily: 'IBM Plex Mono, monospace' }}>
                      <div style={{ fontWeight: 900, fontSize: 13 }}>{d.dispatch_number}</div>
                      <div style={{ fontSize: 11, color: '#475569', marginBottom: 4 }}>{d.customer_name || '—'}</div>
                      <div style={{ fontSize: 11 }}>
                        <b>Destination:</b> {d.destination_city_mock || d.destination}
                      </div>
                      <div style={{ fontSize: 11 }}><b>Status:</b> {d.status}</div>
                      {d.container_number && (
                        <div style={{ fontSize: 11 }}><b>Container:</b> {d.container_number}</div>
                      )}
                      {d.carrier && (
                        <div style={{ fontSize: 11 }}><b>Carrier:</b> {d.carrier}</div>
                      )}
                      <div style={{ fontSize: 11, marginTop: 4 }}>
                        <b>Progress:</b> {d.progress_pct}% · {d.hours_elapsed}h elapsed
                      </div>
                      {d.eta_at && (
                        <div style={{ fontSize: 11 }}>
                          <b>ETA:</b> {new Date(d.eta_at).toLocaleString()}
                        </div>
                      )}
                      <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 4 }}>
                        {d.data_source === 'pop_gps' ? '✓ POP GPS verified' : 'mocked origin — pending POP'}
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>
              </React.Fragment>
            );
          })}
        </MapContainer>
        )}
      </div>

      {data && (
        <div className="flex flex-wrap gap-3 items-center px-4 py-2 border-t border-border text-[10px] uppercase tracking-widest text-muted-foreground bg-muted"
          data-testid="live-shipment-legend">
          {Object.entries(STATUS_COLOURS).slice(0, 4).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: v }} />
              {k}
            </span>
          ))}
          <span className="ml-auto text-muted-foreground font-mono">Mocked GPS · refreshes every 30s</span>
        </div>
      )}
      </div>
    </div>
  );
};

export default LiveShipmentMap;
